import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  FlexBox,
  AlertBox,
  AlertBoxDismissButton,
  Typography,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import PropTypes from 'prop-types';
import {
  trackAnonymousOrderTransferBannerPresentedToShopper,
  trackAnonymousOrderViewedByLoggedInShopper,
} from '../../../apis/newRelicTrackingApi';
import useSiteData from '../../../hooks/useSiteData';
import useIdentityContext from '../../../hooks/useIdentityContext';
import { AnonymousOrderTransferBannerText } from './AnonymousOrderTransferBannerText';
import useAnonymousOrderTransfer from '../../../hooks/useAnonymousOrderTransfer';
import { API_STATUS } from '../../../constants/apiStatus';

/*
  If a signed in shopper is attempting to access an anonymously placed order which we have determined they own,
  fetch the total number of orders they placed in that same anonymous session and prompt them with the option 
  to transfer those orders (and their corrsponding assets) to their account.
*/
export const AnonymousOrderTransferBanner = ({ order }) => {
  const [orderTransferBannerMessages, sharedActionMessages] = useTranslations([
    'orderTransferBannerMessages',
    'sharedActionMessages',
  ]);
  const { orderPlacedAnonymously, orderNumber } = order;
  const { identity } = useIdentityContext();
  const { isAnonymousUser, shopperId } = identity;
  const {
    tenantEnvironment,
    selfOrderTransferEnabled: selfOrderTransferFeatureEnabled,
  } = useSiteData();

  const [dismissed, setDismissed] = useState(false);

  const signedInShopperIsViewingAnonOrder =
    !isAnonymousUser && orderPlacedAnonymously;

  const signedInShopperIsEligibleToTransferAnonOrder =
    signedInShopperIsViewingAnonOrder && selfOrderTransferFeatureEnabled;

  const {
    numberOfAnonOrders,
    numberOfAnonOrdersTransferred,
    orderTransferStatus,
    transferAnonymousOrdersToSignedInShopper,
  } = useAnonymousOrderTransfer({
    disabled: !signedInShopperIsEligibleToTransferAnonOrder || dismissed,
    order,
  });

  const showOrderTransferBanner =
    (numberOfAnonOrders > 0 || numberOfAnonOrdersTransferred > 0) && !dismissed;

  // If the shopper is signed in and the order is anonymous, fire NR metric
  useEffect(() => {
    if (signedInShopperIsViewingAnonOrder) {
      const metricsData = {
        orderNumber,
        shopperId,
        tenantEnvironment,
      };
      trackAnonymousOrderViewedByLoggedInShopper(metricsData);
    }
  }, [
    signedInShopperIsViewingAnonOrder,
    orderNumber,
    shopperId,
    tenantEnvironment,
  ]);

  // If the banner is presented to the shopper, fire NR metric
  useEffect(() => {
    if (showOrderTransferBanner) {
      const metricsData = {
        shopperId,
        anonymousShopperId: order.shopperId,
        validationOrderNumber: orderNumber,
        numberOfAnonOrders,
      };
      trackAnonymousOrderTransferBannerPresentedToShopper(metricsData);
    }
  }, [
    showOrderTransferBanner,
    numberOfAnonOrders,
    order.shopperId,
    orderNumber,
    shopperId,
  ]);

  if (showOrderTransferBanner) {
    return (
      <AlertBox
        skin="standard"
        dismissed={dismissed}
        mb={6}
        onRequestDismiss={() => {
          setDismissed(true);
        }}
      >
        <FlexBox
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <Box textAlign="left">
            <AnonymousOrderTransferBannerText
              orderTransferStatus={orderTransferStatus}
              numberOfAnonOrders={numberOfAnonOrders}
              numberOfAnonOrdersTransferred={numberOfAnonOrdersTransferred}
            />
          </Box>
          {(orderTransferStatus === API_STATUS.IDLE ||
            orderTransferStatus === API_STATUS.FAILED) && (
            <Button
              skin="secondary"
              my={4}
              size="mini"
              onClick={() => transferAnonymousOrdersToSignedInShopper()}
            >
              {
                // TODO: remove Typography once SWAN has fixed the bug with the text showing in white
              }
              <Typography textColor="subtle">
                {orderTransferBannerMessages.guestOrderTransferButtonText}
              </Typography>
            </Button>
          )}
          <AlertBoxDismissButton
            visuallyHiddenLabel={sharedActionMessages.close}
          />
        </FlexBox>
      </AlertBox>
    );
  }

  return null;
};

AnonymousOrderTransferBanner.propTypes = {
  order: PropTypes.object.isRequired,
};
